import { takeLatest, call, put } from 'redux-saga/effects';
import moment from 'moment-timezone';
import { timelineActionTypes } from './Timeline.actionTypes';
import memberSessionService from '../../services/memberSessions';
import { filterSessionsByDayToSelectedRange } from '../../utils/timelineSessionsByRangeFilter';

const formatSeconds = (sec = 0) => {
  const minutes = Math.floor(sec / 60);
  return {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  };
};

// Helper function to extract appUsage time from activity payload and convert to seconds
// Handles strings like "2.22min" or numbers in minutes, converts to seconds
const extractAppUsageTimeInSeconds = (timeValue) => {
  if (timeValue === undefined || timeValue === null) return 0;

  // If already a number, assume it's in minutes (from activity payload) and convert to seconds
  if (typeof timeValue === 'number') {
    return Math.round(timeValue * 60);
  }

  // If string, extract numeric part and convert minutes to seconds
  const timeString = String(timeValue);
  let minutes = 0;

  if (timeString.includes('min') || timeString.includes('mins')) {
    const timeSplit = timeString.split(/min/i);
    if (timeSplit.length > 0) {
      minutes = Number(timeSplit[0].trim());
    }
  } else {
    // Try to parse as number directly (assume minutes)
    minutes = Number(timeString);
  }

  if (isNaN(minutes)) minutes = 0;
  return Math.round(minutes * 60); // Convert minutes to seconds
};

/**
 * Check if a session crosses midnight in the given timezone
 */
const crossesMidnight = (startMoment, endMoment, timezone) => {
  if (!endMoment) return false;
  const startDate = startMoment.format('YYYY-MM-DD');
  const endDate = endMoment.format('YYYY-MM-DD');
  return startDate !== endDate;
};

/**
 * Split a session into day segments when it crosses midnight
 * Returns array of segments with calculated metrics
 */
const splitSessionByMidnight = (
  session,
  startMoment,
  endMoment,
  timezone,
  activities = []
) => {
  console.log(
    `[Timeline] 🔄 splitSessionByMidnight: Starting split for session ${session.id}`,
    {
      sessionId: session.id,
      start: startMoment.format('YYYY-MM-DD HH:mm:ss'),
      end: endMoment.format('YYYY-MM-DD HH:mm:ss'),
      timezone,
      totalDurationSec: session.duration || 0,
      totalIdleSec: session.idleDuration || 0,
      activitiesCount: activities.length,
    }
  );

  const segments = [];
  const totalDurationSec = session.duration || 0;
  const totalIdleSec = session.idleDuration || 0;

  // Find all midnight boundaries between start and end
  const boundaries = [];
  const startDate = startMoment.format('YYYY-MM-DD');
  const endDate = endMoment.format('YYYY-MM-DD');

  // Only proceed if dates are different (session crosses midnight)
  if (startDate === endDate) {
    console.log(
      `[Timeline] ⚠️ splitSessionByMidnight: Session ${session.id} does not cross midnight, skipping split`
    );
    return null;
  }

  // Get the midnight boundary (start of the day after session start)
  // This is the first midnight that occurs after the session starts
  let midnightBoundary = startMoment.clone().add(1, 'day').startOf('day');

  // Add all midnight boundaries that occur before the end time
  // Use milliseconds comparison for precision
  while (midnightBoundary.valueOf() < endMoment.valueOf()) {
    boundaries.push(midnightBoundary.clone());
    midnightBoundary.add(1, 'day');

    // Safety check to prevent infinite loop (max 10 days)
    if (boundaries.length > 10) break;
  }

  console.log(
    `[Timeline] 📅 splitSessionByMidnight: Found ${boundaries.length} midnight boundary/boundaries for session ${session.id}`,
    {
      sessionId: session.id,
      boundaries: boundaries.map((b) => b.format('YYYY-MM-DD HH:mm:ss')),
      startDate,
      endDate,
    }
  );

  // If no boundaries, session doesn't cross midnight (shouldn't happen, but safety check)
  if (boundaries.length === 0) {
    console.warn(
      `[Timeline] No boundaries found for session crossing midnight:`,
      {
        start: startMoment.format('YYYY-MM-DD HH:mm:ss'),
        end: endMoment.format('YYYY-MM-DD HH:mm:ss'),
        startDate,
        endDate,
        midnightBoundary: midnightBoundary.format('YYYY-MM-DD HH:mm:ss'),
      }
    );
    return null;
  }

  // Create segments
  // We need boundaries.length + 1 segments:
  // - One segment for each boundary (from previous boundary/start to this boundary)
  // - One final segment (from last boundary to end)
  let segmentStart = startMoment.clone();

  // Create segments up to each boundary
  for (let i = 0; i < boundaries.length; i++) {
    const boundary = boundaries[i];
    // Segment ends at this boundary (midnight)
    const segmentEnd = boundary.clone();

    // Calculate segment duration in seconds
    const segmentDurationSec = Math.max(
      0,
      Math.floor(
        (segmentEnd.toDate().getTime() - segmentStart.toDate().getTime()) / 1000
      )
    );

    // Skip segments with zero or negative duration
    if (segmentDurationSec <= 0) {
      segmentStart = boundary.clone();
      continue;
    }

    // Calculate idle time for this segment from activities
    let segmentIdleSec = 0;
    let idleTimeSource = 'estimated';
    let segmentAppUsage = {}; // Initialize appUsage for this segment

    if (activities.length > 0) {
      // Filter activities that fall within this segment
      // Include activities that are at the start boundary, exclude those at the end boundary
      const segmentActivities = activities.filter((activity) => {
        if (!activity.timestamp) return false;
        const activityMoment = moment(activity.timestamp).tz(timezone);
        return (
          activityMoment.isSameOrAfter(segmentStart) &&
          activityMoment.isBefore(segmentEnd)
        );
      });

      console.log(
        `[Timeline] 📊 splitSessionByMidnight: Segment ${
          i + 1
        } activity filtering`,
        {
          sessionId: session.id,
          segmentIndex: i + 1,
          segmentStart: segmentStart.format('YYYY-MM-DD HH:mm:ss'),
          segmentEnd: segmentEnd.format('YYYY-MM-DD HH:mm:ss'),
          totalActivities: activities.length,
          segmentActivitiesCount: segmentActivities.length,
          segmentDurationSec,
        }
      );

      // Sum idleTime and aggregate appUsage from activities in this segment
      if (segmentActivities.length > 0) {
        segmentIdleSec = segmentActivities.reduce((sum, activity) => {
          return sum + (activity.idleTime || 0);
        }, 0);

        // Aggregate appUsage from activities in this segment
        segmentActivities.forEach((activity) => {
          const activityAppUsage = activity.appUsage || {};
          Object.entries(activityAppUsage).forEach(([appPath, timeValue]) => {
            if (appPath && timeValue !== undefined && timeValue !== null) {
              // Process the timeValue (could be string like "2.22min" or number in minutes)
              const seconds = extractAppUsageTimeInSeconds(timeValue);
              segmentAppUsage[appPath] =
                (segmentAppUsage[appPath] || 0) + seconds;
            }
          });
        });

        idleTimeSource = 'activities';
        console.log(
          `[Timeline] ✅ splitSessionByMidnight: Segment ${
            i + 1
          } using activities data`,
          {
            sessionId: session.id,
            segmentIndex: i + 1,
            segmentIdleSec,
            idleTimeSource,
            appUsageKeys: Object.keys(segmentAppUsage).length,
          }
        );
      } else {
        // No activities in this segment, use proportional allocation
        if (totalDurationSec > 0) {
          const segmentRatio = segmentDurationSec / totalDurationSec;
          segmentIdleSec = Math.floor(totalIdleSec * segmentRatio);
        } else {
          segmentIdleSec = 0;
        }
        idleTimeSource = 'estimated';
        console.log(
          `[Timeline] ⚠️ splitSessionByMidnight: Segment ${
            i + 1
          } no activities, using estimated idle time`,
          {
            sessionId: session.id,
            segmentIndex: i + 1,
            segmentIdleSec,
            segmentRatio:
              totalDurationSec > 0 ? segmentDurationSec / totalDurationSec : 0,
          }
        );
      }
    } else {
      // No activities available, use proportional allocation
      if (totalDurationSec > 0) {
        const segmentRatio = segmentDurationSec / totalDurationSec;
        segmentIdleSec = Math.floor(totalIdleSec * segmentRatio);
      } else {
        segmentIdleSec = 0;
      }
      idleTimeSource = 'estimated';
      console.log(
        `[Timeline] ⚠️ splitSessionByMidnight: Segment ${
          i + 1
        } no activities available for session, using estimated idle time`,
        {
          sessionId: session.id,
          segmentIndex: i + 1,
          segmentIdleSec,
          segmentRatio:
            totalDurationSec > 0 ? segmentDurationSec / totalDurationSec : 0,
        }
      );
    }

    // Ensure idle time doesn't exceed segment duration
    segmentIdleSec = Math.min(segmentIdleSec, segmentDurationSec);

    const segmentProductiveSec = Math.max(
      0,
      segmentDurationSec - segmentIdleSec
    );
    const segmentActivityPct = segmentDurationSec
      ? (segmentProductiveSec / segmentDurationSec) * 100
      : 0;

    const dayKey = segmentStart.format('YYYY-MM-DD');
    const dayLabel = segmentStart.format('ddd, MMM D, YYYY');

    // Check if this segment is on a different day than the session start
    const isNextDay =
      segmentStart.format('YYYY-MM-DD') !== startMoment.format('YYYY-MM-DD');

    // If no activities, use proportional allocation for appUsage
    if (Object.keys(segmentAppUsage).length === 0 && totalDurationSec > 0) {
      const sessionAppUsage = session.appUsage || {};
      const segmentRatio = segmentDurationSec / totalDurationSec;
      Object.entries(sessionAppUsage).forEach(([appPath, seconds]) => {
        if (appPath && seconds) {
          segmentAppUsage[appPath] = Math.floor(Number(seconds) * segmentRatio);
        }
      });
    }

    const segmentData = {
      id: `${session.id}_segment_${i + 1}`,
      parentSessionId: session.id,
      start: segmentStart.toISOString(),
      end: segmentEnd.toISOString(),
      durationSec: segmentDurationSec,
      idleSec: segmentIdleSec,
      productiveSec: segmentProductiveSec,
      activityPct: segmentActivityPct,
      appUsage: segmentAppUsage,
      isSplit: true,
      isNextDay,
      idleTimeSource,
      dayKey,
      dayLabel,
    };

    console.log(
      `[Timeline] ✨ splitSessionByMidnight: Created segment ${i + 1}`,
      {
        sessionId: session.id,
        segmentId: segmentData.id,
        dayKey,
        dayLabel,
        start: segmentStart.format('YYYY-MM-DD HH:mm:ss'),
        end: segmentEnd.format('YYYY-MM-DD HH:mm:ss'),
        durationSec: segmentDurationSec,
        idleSec: segmentIdleSec,
        productiveSec: segmentProductiveSec,
        activityPct: segmentActivityPct,
        idleTimeSource,
        isNextDay,
        appUsageKeys: Object.keys(segmentAppUsage).length,
      }
    );

    segments.push(segmentData);

    segmentStart = boundary.clone();
  }

  // Create final segment from last boundary to end
  if (boundaries.length > 0) {
    const finalSegmentStart = boundaries[boundaries.length - 1].clone();
    const finalSegmentEnd = endMoment.clone();
    const finalSegmentDurationSec = Math.max(
      0,
      Math.floor(
        (finalSegmentEnd.toDate().getTime() -
          finalSegmentStart.toDate().getTime()) /
          1000
      )
    );

    if (finalSegmentDurationSec > 0) {
      // Calculate idle time for final segment
      let finalSegmentIdleSec = 0;
      let finalIdleTimeSource = 'estimated';

      if (activities.length > 0) {
        const finalSegmentActivities = activities.filter((activity) => {
          if (!activity.timestamp) return false;
          const activityMoment = moment(activity.timestamp).tz(timezone);
          return (
            activityMoment.isSameOrAfter(finalSegmentStart) &&
            activityMoment.isBefore(finalSegmentEnd)
          );
        });

        console.log(
          `[Timeline] 📊 splitSessionByMidnight: Final segment activity filtering`,
          {
            sessionId: session.id,
            segmentIndex: boundaries.length + 1,
            segmentStart: finalSegmentStart.format('YYYY-MM-DD HH:mm:ss'),
            segmentEnd: finalSegmentEnd.format('YYYY-MM-DD HH:mm:ss'),
            totalActivities: activities.length,
            finalSegmentActivitiesCount: finalSegmentActivities.length,
            finalSegmentDurationSec,
          }
        );

        if (finalSegmentActivities.length > 0) {
          finalSegmentIdleSec = finalSegmentActivities.reduce(
            (sum, activity) => {
              return sum + (activity.idleTime || 0);
            },
            0
          );
          finalIdleTimeSource = 'activities';
          console.log(
            `[Timeline] ✅ splitSessionByMidnight: Final segment using activities data`,
            {
              sessionId: session.id,
              finalSegmentIdleSec,
              finalIdleTimeSource,
            }
          );
        } else {
          if (totalDurationSec > 0) {
            const segmentRatio = finalSegmentDurationSec / totalDurationSec;
            finalSegmentIdleSec = Math.floor(totalIdleSec * segmentRatio);
          }
          console.log(
            `[Timeline] ⚠️ splitSessionByMidnight: Final segment no activities, using estimated idle time`,
            {
              sessionId: session.id,
              finalSegmentIdleSec,
              segmentRatio:
                totalDurationSec > 0
                  ? finalSegmentDurationSec / totalDurationSec
                  : 0,
            }
          );
        }
      } else {
        if (totalDurationSec > 0) {
          const segmentRatio = finalSegmentDurationSec / totalDurationSec;
          finalSegmentIdleSec = Math.floor(totalIdleSec * segmentRatio);
        }
        console.log(
          `[Timeline] ⚠️ splitSessionByMidnight: Final segment no activities available, using estimated idle time`,
          {
            sessionId: session.id,
            finalSegmentIdleSec,
            segmentRatio:
              totalDurationSec > 0
                ? finalSegmentDurationSec / totalDurationSec
                : 0,
          }
        );
      }

      finalSegmentIdleSec = Math.min(
        finalSegmentIdleSec,
        finalSegmentDurationSec
      );
      const finalSegmentProductiveSec = Math.max(
        0,
        finalSegmentDurationSec - finalSegmentIdleSec
      );
      const finalSegmentActivityPct = finalSegmentDurationSec
        ? (finalSegmentProductiveSec / finalSegmentDurationSec) * 100
        : 0;

      // Aggregate appUsage for final segment
      let finalSegmentAppUsage = {};
      if (activities.length > 0) {
        const finalSegmentActivities = activities.filter((activity) => {
          if (!activity.timestamp) return false;
          const activityMoment = moment(activity.timestamp).tz(timezone);
          return (
            activityMoment.isSameOrAfter(finalSegmentStart) &&
            activityMoment.isBefore(finalSegmentEnd)
          );
        });

        if (finalSegmentActivities.length > 0) {
          finalSegmentActivities.forEach((activity) => {
            const activityAppUsage = activity.appUsage || {};
            Object.entries(activityAppUsage).forEach(([appPath, timeValue]) => {
              if (appPath && timeValue !== undefined && timeValue !== null) {
                // Process the timeValue (could be string like "2.22min" or number in minutes)
                const seconds = extractAppUsageTimeInSeconds(timeValue);
                finalSegmentAppUsage[appPath] =
                  (finalSegmentAppUsage[appPath] || 0) + seconds;
              }
            });
          });
        } else {
          // No activities, use proportional allocation
          if (totalDurationSec > 0) {
            const sessionAppUsage = session.appUsage || {};
            const segmentRatio = finalSegmentDurationSec / totalDurationSec;
            Object.entries(sessionAppUsage).forEach(([appPath, seconds]) => {
              if (appPath && seconds) {
                finalSegmentAppUsage[appPath] = Math.floor(
                  Number(seconds) * segmentRatio
                );
              }
            });
          }
        }
      } else {
        // No activities available, use proportional allocation
        if (totalDurationSec > 0) {
          const sessionAppUsage = session.appUsage || {};
          const segmentRatio = finalSegmentDurationSec / totalDurationSec;
          Object.entries(sessionAppUsage).forEach(([appPath, seconds]) => {
            if (appPath && seconds) {
              finalSegmentAppUsage[appPath] = Math.floor(
                Number(seconds) * segmentRatio
              );
            }
          });
        }
      }

      const finalDayKey = finalSegmentStart.format('YYYY-MM-DD');
      const finalDayLabel = finalSegmentStart.format('ddd, MMM D, YYYY');
      const finalIsNextDay =
        finalSegmentStart.format('YYYY-MM-DD') !==
        startMoment.format('YYYY-MM-DD');

      const finalSegmentData = {
        id: `${session.id}_segment_${boundaries.length + 1}`,
        parentSessionId: session.id,
        start: finalSegmentStart.toISOString(),
        end: finalSegmentEnd.toISOString(),
        durationSec: finalSegmentDurationSec,
        idleSec: finalSegmentIdleSec,
        productiveSec: finalSegmentProductiveSec,
        activityPct: finalSegmentActivityPct,
        appUsage: finalSegmentAppUsage,
        isSplit: true,
        isNextDay: finalIsNextDay,
        idleTimeSource: finalIdleTimeSource,
        dayKey: finalDayKey,
        dayLabel: finalDayLabel,
      };

      console.log(
        `[Timeline] ✨ splitSessionByMidnight: Created final segment`,
        {
          sessionId: session.id,
          segmentId: finalSegmentData.id,
          dayKey: finalDayKey,
          dayLabel: finalDayLabel,
          start: finalSegmentStart.format('YYYY-MM-DD HH:mm:ss'),
          end: finalSegmentEnd.format('YYYY-MM-DD HH:mm:ss'),
          durationSec: finalSegmentDurationSec,
          idleSec: finalSegmentIdleSec,
          productiveSec: finalSegmentProductiveSec,
          activityPct: finalSegmentActivityPct,
          idleTimeSource: finalIdleTimeSource,
          isNextDay: finalIsNextDay,
          appUsageKeys: Object.keys(finalSegmentAppUsage).length,
        }
      );

      segments.push(finalSegmentData);
    }
  }

  console.log(
    `[Timeline] ✅ splitSessionByMidnight: Completed split for session ${session.id}`,
    {
      sessionId: session.id,
      totalSegments: segments.length,
      segments: segments.map((s) => ({
        id: s.id,
        dayKey: s.dayKey,
        durationSec: s.durationSec,
        idleSec: s.idleSec,
        idleTimeSource: s.idleTimeSource,
      })),
    }
  );

  return segments;
};

/**
 * Batch fetch activities for multiple sessions
 */
const fetchActivitiesForSessions = async (
  sessions,
  teamId,
  userUID,
  companyId,
  employeeId
) => {
  console.log(
    `[Timeline] 🔍 fetchActivitiesForSessions: Starting batch fetch for ${sessions.length} session(s)`,
    {
      sessionIds: sessions.map((s) => s.id),
      teamId,
      userUID,
      companyId,
      employeeId,
    }
  );

  const activitiesMap = {};

  // Create promises for all activity fetches
  const fetchPromises = sessions.map(async (session) => {
    try {
      const response = await memberSessionService.getSessionActivities({
        teamId,
        userUID,
        companyId,
        employeeId,
        sessionId: session.id,
        sessionStartTime: session.startTime, // Pass session startTime to determine path
      });

      if (response?.success && response?.data?.activities) {
        console.log(
          `[Timeline] ✅ fetchActivitiesForSessions: Successfully fetched activities for session ${session.id}`,
          {
            sessionId: session.id,
            activitiesCount: response.data.activities.length,
          }
        );
        return {
          sessionId: session.id,
          activities: response.data.activities,
        };
      }
      console.log(
        `[Timeline] ⚠️ fetchActivitiesForSessions: No activities returned for session ${session.id}`,
        {
          sessionId: session.id,
          responseSuccess: response?.success,
          hasActivities: !!response?.data?.activities,
        }
      );
      return {
        sessionId: session.id,
        activities: [],
      };
    } catch (error) {
      console.warn(
        `[Timeline] ❌ fetchActivitiesForSessions: Failed to fetch activities for session ${session.id}:`,
        error
      );
      return {
        sessionId: session.id,
        activities: [],
      };
    }
  });

  // Wait for all fetches to complete
  const results = await Promise.all(fetchPromises);

  // Build map of sessionId -> activities
  let successCount = 0;
  let emptyCount = 0;
  results.forEach((result) => {
    activitiesMap[result.sessionId] = result.activities;
    if (result.activities.length > 0) {
      successCount++;
    } else {
      emptyCount++;
    }
  });

  console.log(
    `[Timeline] ✅ fetchActivitiesForSessions: Batch fetch completed`,
    {
      totalSessions: sessions.length,
      sessionsWithActivities: successCount,
      sessionsWithoutActivities: emptyCount,
      activitiesMapKeys: Object.keys(activitiesMap).length,
    }
  );

  return activitiesMap;
};

function* getSessionsSaga({ payload }) {
  const {
    teamId,
    userUID,
    companyId,
    employeeId,
    memberId,
    startDate,
    endDate,
    timeZone,
    page = 1,
    pageSize = 7,
    rate = 0,
    forExport = false,
  } = payload || {};

  try {
    yield put({
      type: timelineActionTypes.SET_STATE,
      payload: {
        ...(forExport
          ? { exportLoading: true }
          : { loading: true, error: null }),
      },
    });

    const tz = timeZone || moment.tz.guess();
    const response = yield call(memberSessionService.getSessionsForRange, {
      teamId,
      userUID,
      companyId,
      employeeId,
      startDate,
      endDate,
      timeZone: tz,
    });

    if (!response?.success) {
      throw response?.error || new Error('Failed to fetch sessions');
    }

    const rawSessions = response?.data?.sessions || [];
    const grouped = {};

    // Step 1: Identify sessions that cross midnight
    const sessionsNeedingActivities = [];
    const sessionMetadata = [];

    rawSessions.forEach((session) => {
      const start = session.startTime?.toDate
        ? session.startTime.toDate()
        : null;
      const endRaw = session.endTime?.toDate
        ? session.endTime.toDate()
        : session.lastPingAt?.toDate
          ? session.lastPingAt.toDate()
          : null;
      if (!start) return;

      const startMoment = moment(start).tz(tz);
      const endMoment = endRaw ? moment(endRaw).tz(tz) : null;

      // Check if session crosses midnight
      if (endMoment && crossesMidnight(startMoment, endMoment, tz)) {
        console.log(
          `[Timeline] 🌙 GET_SESSIONS: Session ${session.id} crosses midnight`,
          {
            sessionId: session.id,
            start: startMoment.format('YYYY-MM-DD HH:mm:ss'),
            end: endMoment.format('YYYY-MM-DD HH:mm:ss'),
            startDate: startMoment.format('YYYY-MM-DD'),
            endDate: endMoment.format('YYYY-MM-DD'),
            timezone: tz,
          }
        );
        sessionsNeedingActivities.push(session);
      }

      sessionMetadata.push({
        session,
        startMoment,
        endMoment,
      });
    });

    console.log(`[Timeline] 📊 GET_SESSIONS: Session analysis complete`, {
      totalSessions: rawSessions.length,
      sessionsCrossingMidnight: sessionsNeedingActivities.length,
      sessionIdsCrossingMidnight: sessionsNeedingActivities.map((s) => s.id),
    });

    // Step 2: Batch fetch activities for sessions that cross midnight
    let activitiesMap = {};
    if (sessionsNeedingActivities.length > 0) {
      console.log(
        `[Timeline] 🔍 GET_SESSIONS: Fetching activities for ${sessionsNeedingActivities.length} session(s) that cross midnight`
      );
      activitiesMap = yield call(
        fetchActivitiesForSessions,
        sessionsNeedingActivities,
        teamId,
        userUID,
        companyId,
        employeeId
      );
      console.log(
        `[Timeline] ✅ GET_SESSIONS: Activities fetched, map contains ${
          Object.keys(activitiesMap).length
        } session(s)`,
        {
          sessionIdsWithActivities: Object.keys(activitiesMap),
          activitiesCounts: Object.entries(activitiesMap).map(([id, acts]) => ({
            sessionId: id,
            count: acts.length,
          })),
        }
      );
    } else {
      console.log(
        `[Timeline] ℹ️ GET_SESSIONS: No sessions cross midnight, skipping activity fetch`
      );
    }

    // Step 3: Process all sessions
    sessionMetadata.forEach(({ session, startMoment, endMoment }) => {
      const durationSec = session.duration || 0;
      const idleSec = session.idleDuration || 0;
      const productiveSec = Math.max(0, durationSec - idleSec);
      const activityPct = durationSec ? (productiveSec / durationSec) * 100 : 0;

      // Check if session crosses midnight
      if (endMoment && crossesMidnight(startMoment, endMoment, tz)) {
        // Split session into segments
        const activities = activitiesMap[session.id] || [];
        console.log(
          `[Timeline] 🔄 GET_SESSIONS: Splitting session ${session.id} into segments`,
          {
            sessionId: session.id,
            activitiesCount: activities.length,
            hasActivitiesInMap: !!activitiesMap[session.id],
          }
        );

        const segments = splitSessionByMidnight(
          session,
          startMoment,
          endMoment,
          tz,
          activities
        );

        if (segments && segments.length > 0) {
          console.log(
            `[Timeline] ✅ GET_SESSIONS: Successfully split session ${session.id} into ${segments.length} segment(s)`,
            {
              sessionId: session.id,
              segmentCount: segments.length,
              segmentDays: segments.map((s) => s.dayKey),
            }
          );

          // Add each segment to appropriate day
          segments.forEach((segment) => {
            const dayKey = segment.dayKey;
            const dayLabel = segment.dayLabel;

            if (!grouped[dayKey]) {
              grouped[dayKey] = {
                dayKey,
                dayLabel,
                totals: {
                  durationSec: 0,
                  idleSec: 0,
                  productiveSec: 0,
                  earnings: 0,
                },
                sessions: [],
              };
            }

            grouped[dayKey].sessions.push({
              id: segment.id,
              parentSessionId: segment.parentSessionId,
              start: segment.start,
              end: segment.end,
              durationSec: segment.durationSec,
              idleSec: segment.idleSec,
              productiveSec: segment.productiveSec,
              activityPct: segment.activityPct,
              appUsage: segment.appUsage || {},
              pastData: !!session.pastData,
              typeLog: session.typeLog || null,
              isSplit: true,
              isNextDay: segment.isNextDay,
              idleTimeSource: segment.idleTimeSource,
            });

            grouped[dayKey].totals.durationSec += segment.durationSec;
            grouped[dayKey].totals.idleSec += segment.idleSec;
            grouped[dayKey].totals.productiveSec += segment.productiveSec;
          });
        } else {
          // Fallback: if splitting failed, use original logic
          console.warn(
            `[Timeline] ⚠️ GET_SESSIONS: Splitting failed for session ${session.id}, using fallback logic`,
            {
              sessionId: session.id,
              segmentsResult: segments,
            }
          );

          const dayKey = startMoment.format('YYYY-MM-DD');
          const dayLabel = startMoment.format('ddd, MMM D, YYYY');

          if (!grouped[dayKey]) {
            grouped[dayKey] = {
              dayKey,
              dayLabel,
              totals: {
                durationSec: 0,
                idleSec: 0,
                productiveSec: 0,
                earnings: 0,
              },
              sessions: [],
            };
          }

          grouped[dayKey].sessions.push({
            id: session.id,
            start: startMoment.toISOString(),
            end: endMoment ? endMoment.toISOString() : null,
            durationSec,
            idleSec,
            productiveSec,
            activityPct,
            appUsage: session.appUsage || {},
            pastData: !!session.pastData,
            typeLog: session.typeLog || null,
          });

          grouped[dayKey].totals.durationSec += durationSec;
          grouped[dayKey].totals.idleSec += idleSec;
          grouped[dayKey].totals.productiveSec += productiveSec;
        }
      } else {
        // Session doesn't cross midnight, use original logic
        const dayKey = startMoment.format('YYYY-MM-DD');
        const dayLabel = startMoment.format('ddd, MMM D, YYYY');

        if (!grouped[dayKey]) {
          grouped[dayKey] = {
            dayKey,
            dayLabel,
            totals: {
              durationSec: 0,
              idleSec: 0,
              productiveSec: 0,
              earnings: 0,
            },
            sessions: [],
          };
        }

        grouped[dayKey].sessions.push({
          id: session.id,
          start: startMoment.toISOString(),
          end: endMoment ? endMoment.toISOString() : null,
          durationSec,
          idleSec,
          productiveSec,
          activityPct,
          appUsage: session.appUsage || {},
          pastData: !!session.pastData,
          typeLog: session.typeLog || null,
        });

        grouped[dayKey].totals.durationSec += durationSec;
        grouped[dayKey].totals.idleSec += idleSec;
        grouped[dayKey].totals.productiveSec += productiveSec;
      }
    });

    const daysArray = Object.values(grouped).sort((a, b) =>
      a.dayKey > b.dayKey ? -1 : 1
    );

    const processed = daysArray.map((day) => {
      const { durationSec, idleSec, productiveSec } = day.totals;
      const activityPct = durationSec ? (productiveSec / durationSec) * 100 : 0;
      const earnings = rate ? ((durationSec / 3600) * rate).toFixed(2) : null;
      return {
        ...day,
        totals: {
          durationSec,
          idleSec,
          productiveSec,
          activityPct,
          earnings,
          duration: formatSeconds(durationSec),
          idle: formatSeconds(idleSec),
          productive: formatSeconds(productiveSec),
        },
        sessions: day.sessions.sort((a, b) => (a.start > b.start ? -1 : 1)),
      };
    });

    // Same `sessionsByDay` is used by Timeline, App Usage, Screenshots, Dashboard overview.
    const rangeFiltered = filterSessionsByDayToSelectedRange(
      processed,
      startDate,
      endDate,
      tz
    );

    const totalDays = rangeFiltered.length;
    const startIndex = (page - 1) * pageSize;
    //don't slice the data for now
    const pageData = rangeFiltered;

    yield put({
      type: timelineActionTypes.SET_STATE,
      payload: {
        ...(forExport ? { exportLoading: false } : { loading: false }),
        sessionsByDay: pageData,
        page,
        pageSize,
        totalDays,
        ...(forExport ? { exportData: rangeFiltered } : { exportData: null }),
        filters: {
          memberId,
          userUID,
          startDate,
          endDate,
          timeZone: tz,
        },
      },
    });
  } catch (error) {
    yield put({
      type: timelineActionTypes.SET_STATE,
      payload: {
        loading: false,
        exportLoading: false,
        exportData: null,
        error: error?.message || 'Failed to load timeline',
      },
    });
  }
}

export default function* timelineWatcher() {
  yield takeLatest(timelineActionTypes.GET_SESSIONS, getSessionsSaga);
}
